/**
 * BugReportWidget.tsx
 * Глобальный виджет отчёта об ошибке.
 * Плавающая кнопка в нижнем правом углу экрана + модальное окно сбора данных.
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { getCapturedErrors, clearCapturedErrors } from '@/lib/errorCollector';
import BugReportsAPI from '@/api/bugReports.api';
import toast from 'react-hot-toast';
import { getUserRoleLabel } from '@/lib/permissions';

export default function BugReportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [includeLogs, setIncludeLogs] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useAuthStore();
  const location = useLocation();

  const capturedErrors = getCapturedErrors();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setDescription('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim() && capturedErrors.length === 0) {
      toast.error('Пожалуйста, опишите проблему');
      return;
    }

    try {
      setIsSubmitting(true);

      const pageUrl = window.location.href;
      const route = location.pathname + location.search + location.hash;

      await BugReportsAPI.sendReport({
        description: description.trim(),
        pageUrl,
        route,
        userId: user?.id || (user as any)?._id || '',
        userName: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'Гость',
        userRole: user?.userRole || 'guest',
        userEmail: user?.email || '',
        consoleLogs: includeLogs ? capturedErrors : [],
        extraContext: {
          path: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        userAgent: navigator.userAgent,
        screen: `${window.innerWidth}x${window.innerHeight}`,
      });

      toast.success('Отчёт об ошибке отправлен! Спасибо за обратную связь.');
      clearCapturedErrors();
      handleClose();
    } catch (err: any) {
      toast.error(`Ошибка при отправке: ${err.message || 'Сбой сети'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOnlineLesson = location.pathname.includes('/online');

  return (
    <>
      {/* ── Плавающая кнопка ── */}
      <button
        onClick={handleOpen}
        title="Сообщить об ошибке разработчикам"
        style={{
          position: 'fixed',
          bottom: isOnlineLesson ? '75px' : '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          borderRadius: '30px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#ffffff',
          border: 'none',
          boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0) scale(1)')}
      >
        <span style={{ fontSize: '16px', display: 'inline-block' }}>🐛</span>
        <span>Сообщить об ошибке</span>
        {capturedErrors.length > 0 && (
          <span
            style={{
              background: '#ffffff',
              color: '#dc2626',
              borderRadius: '10px',
              padding: '1px 6px',
              fontSize: '11px',
              fontWeight: 700,
            }}
          >
            {capturedErrors.length}
          </span>
        )}
      </button>

      {/* ── Модальное окно ── */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            padding: '16px',
          }}
          onClick={handleClose}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '520px',
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              animation: 'fadeIn 0.2s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Шапка */}
            <div
              style={{
                padding: '18px 24px',
                background: 'linear-gradient(90deg, #1e293b, #0f172a)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🐛</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Сообщить об ошибке</h3>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
                    Автоматический отчёт разработчикам
                  </div>
                </div>
              </div>
              <button
                onClick={handleClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Тело */}
            <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Контекстная карточка */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  fontSize: '12px',
                  color: '#475569',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                <div>
                  <strong style={{ color: '#1e293b' }}>Страница:</strong>{' '}
                  <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', wordBreak: 'break-all' }}>
                    {location.pathname + location.search + location.hash || '/'}
                  </code>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <span>
                    <strong style={{ color: '#1e293b' }}>Пользователь:</strong> {user?.name || user?.email || 'Гость'}
                  </span>
                  <span>
                    <strong style={{ color: '#1e293b' }}>Роль:</strong> {getUserRoleLabel(user?.userRole)}
                  </span>
                </div>
              </div>

              {/* Поле ввода */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                  Что именно пошло не так?
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Опишите, какие действия вы совершали и что произошло (например: 'При нажатии на кнопку Проверить вылез белый экран')..."
                  rows={4}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ef4444')}
                  onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                />
              </div>

              {/* Чекбокс логов */}
              {capturedErrors.length > 0 && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '12px',
                    color: '#475569',
                    cursor: 'pointer',
                    background: '#fef2f2',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #fecaca',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={includeLogs}
                    onChange={(e) => setIncludeLogs(e.target.checked)}
                    style={{ accentColor: '#dc2626', width: '16px', height: '16px' }}
                  />
                  <span>
                    Прикрепить последние <strong>{capturedErrors.length}</strong> ошибок консоли браузера для точной отладки
                  </span>
                </label>
              )}

              {/* Кнопки действия */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                  }}
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить отчёт'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
